import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { MapPin, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react'
import {
  useServiceAreasAdmin,
  useCreateServiceArea,
  useUpdateServiceArea,
  useDeleteServiceArea,
} from '@/hooks/data/useCatalogAdmin'
import type { CatalogAreaAdmin, CatalogAreaInput } from '@/types/domain'
import EditModal from '@/components/crud/EditModal'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'

const toLines = (arr?: string[]) => (arr ?? []).join('\n')
const fromLines = (text: string) =>
  String(text ?? '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)

function AreasPage() {
  const { data: areas = [], isLoading, isError, error, isFetching, refetch } = useServiceAreasAdmin()
  const create = useCreateServiceArea()
  const update = useUpdateServiceArea()
  const del = useDeleteServiceArea()
  const confirmDelete = useDeleteConfirm()
  const [creating, setCreating] = useState(false)
  const [q, setQ] = useState('')
  const [editing, setEditing] = useState<CatalogAreaAdmin | null>(null)

  const rows = areas.filter((a) => !q || a.name.toLowerCase().includes(q.toLowerCase()))

  const fields = [
    { key: 'code', label: 'Code', required: true },
    { key: 'name', label: 'Name', required: true },
    { key: 'priceFactor', label: 'Price Factor', type: 'number' as const, required: true, placeholder: '0.5–2.0' },
    { key: 'suburbs', label: 'Suburbs', type: 'textarea' as const, placeholder: 'One suburb per line' },
    { key: 'description', label: 'Description', type: 'textarea' as const },
    { key: 'imageUrl', label: 'Image URL', placeholder: 'https://…' },
    { key: 'galleryImages', label: 'Gallery images', type: 'textarea' as const, placeholder: 'One image URL per line' },
    { key: 'priceRange', label: 'Price range', placeholder: '$$' },
    { key: 'population', label: 'Population' },
    { key: 'nearby', label: 'Nearby areas', type: 'textarea' as const, placeholder: 'One area name per line' },
    { key: 'latitude', label: 'Latitude', type: 'number' as const },
    { key: 'longitude', label: 'Longitude', type: 'number' as const },
    {
      key: 'featured',
      label: 'Featured',
      type: 'select' as const,
      required: true,
      options: [
        { value: 'false', label: 'No' },
        { value: 'true', label: 'Yes' },
      ],
    },
    {
      key: 'active',
      label: 'Active',
      type: 'select' as const,
      required: true,
      options: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' },
      ],
    },
  ]

  const toInput = (values: Record<string, any>): CatalogAreaInput => ({
    code: values.code,
    name: values.name,
    priceFactor: Number(values.priceFactor),
    suburbs: fromLines(values.suburbs),
    description: values.description || undefined,
    imageUrl: values.imageUrl || undefined,
    galleryImages: fromLines(values.galleryImages),
    priceRange: values.priceRange || undefined,
    population: values.population || undefined,
    nearby: fromLines(values.nearby),
    featured: values.featured === 'true',
    latitude: values.latitude ? Number(values.latitude) : undefined,
    longitude: values.longitude ? Number(values.longitude) : undefined,
    active: values.active === 'true',
  })

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <MapPin className="w-6 h-6" /> Service Areas
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Manage covered suburbs and pricing factors (CatalogAdminController).</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setCreating(true)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm">
            <Plus className="w-4 h-4" /> New Area
          </button>
        </div>
      </div>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search areas…"
        className="w-full md:w-72 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
      />

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading areas…</p>
        ) : isError ? (
          <p className="p-6 text-red-600">Failed to load areas: {(error as Error)?.message}.</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-gray-500">No areas found.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Code</th>
                <th className="px-4 py-2">Price Factor</th>
                <th className="px-4 py-2">Featured</th>
                <th className="px-4 py-2">Active</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800 dark:text-gray-100">{a.name}</div>
                    <div className="text-xs text-gray-500">{a.suburbs.slice(0, 3).join(', ')}{a.suburbs.length > 3 ? '…' : ''}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{a.code}</td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{a.priceFactor}x</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{a.featured ? 'Yes' : '—'}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        a.active
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {a.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEditing(a)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Edit area">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          confirmDelete(a.name, async () => {
                            await del.mutateAsync(a.id)
                          })
                        }
                        className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                        title="Delete area"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <EditModal
        isOpen={creating}
        onClose={() => setCreating(false)}
        title="New Service Area"
        fields={fields}
        initialValues={{ featured: 'false', active: 'true' }}
        submitLabel="Create"
        onSubmit={async (values) => {
          await create.mutateAsync(toInput(values))
        }}
      />

      <EditModal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title="Edit Service Area"
        description={editing?.name}
        fields={fields}
        initialValues={
          editing
            ? {
                code: editing.code,
                name: editing.name,
                priceFactor: editing.priceFactor,
                suburbs: toLines(editing.suburbs),
                description: editing.description ?? '',
                imageUrl: editing.imageUrl ?? '',
                galleryImages: toLines(editing.galleryImages),
                priceRange: editing.priceRange ?? '',
                population: editing.population ?? '',
                nearby: toLines(editing.nearby),
                latitude: editing.latitude ?? '',
                longitude: editing.longitude ?? '',
                featured: editing.featured ? 'true' : 'false',
                active: editing.active ? 'true' : 'false',
              }
            : {}
        }
        onSubmit={async (values) => {
          if (!editing) return
          await update.mutateAsync({ id: editing.id, body: toInput(values) })
        }}
      />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/areas/')({
  component: AreasPage,
})
